import { useContext, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { PasswordField } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { apiUserClient } from "@/api";
import { AuthStateContext } from "@/contexts/AuthContext";

interface ChangePasswordFormData {
  oldpassword?: string;
  newpassword: string;
  confirmpassword: string;
}

interface ChangePasswordProps {
  oldPasswordRequired?: boolean;
}

export const ChangePassword: React.FC<ChangePasswordProps> = ({
  oldPasswordRequired = true,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { authSteps, verifyPasswordStrength } = useContext(AuthStateContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<ChangePasswordFormData>();

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const hasTempPassword =
    authSteps.filter((step) => step.includes("changepassword")).length > 0;

  const onSubmit = async (formData: ChangePasswordFormData) => {
    const passed = await checkPasswords({
      oldPasswordRequired,
      formdata: formData,
      verifyPasswordStrength,
    });

    if (!passed) {
      return;
    }

    setLoading(true);

    try {
      if (oldPasswordRequired) {
        const data = {
          old: formData.oldpassword,
          new: formData.newpassword,
        };

        await apiUserClient.ChangePassword(data);
        toast.success(
          "Great! Your password has been updated successfully. " +
            "Please log in using your new password.",
          { autoClose: 10000 },
        );
        reset();
        // Redirect to login or logout
        window.location.href = "/login";
      } else {
        const token = new URL(document.URL).searchParams.get("token") || "";
        const data = {
          new: formData.newpassword,
          token: token,
        };

        await apiUserClient.ResetPassword(data);
        toast.dismiss();
        toast.success(
          "Great! Your password has been updated successfully. " +
            "Please log in using your new password :)",
          { autoClose: 10000 },
        );
        reset();
        navigate("/login");
      }
    } catch (error) {
      console.error("Password change error:", error);
      toast.error("Something went wrong. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          align="center"
          sx={{ mb: 3 }}
        >
          Set a new password
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          {oldPasswordRequired && (
            <Box sx={{ mb: 2 }}>
              <PasswordField
                label={
                  hasTempPassword ? "Temporary Password" : "Existing Password"
                }
                fullWidth
                required
                error={!!errors.oldpassword}
                helperText={errors.oldpassword?.message}
                showPassword={showOldPassword}
                setShowPassword={setShowOldPassword}
                {...register("oldpassword", {
                  required: "Existing password is required",
                })}
              />
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <PasswordField
              label="New Password"
              fullWidth
              required
              error={!!errors.newpassword}
              helperText={errors.newpassword?.message}
              showPassword={showNewPassword}
              setShowPassword={setShowNewPassword}
              {...register("newpassword", {
                required: "New password is required",
              })}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <PasswordField
              label="Confirm Password"
              fullWidth
              required
              error={!!errors.confirmpassword}
              helperText={errors.confirmpassword?.message}
              showPassword={showConfirmPassword}
              setShowPassword={setShowConfirmPassword}
              {...register("confirmpassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === watch("newpassword") || "Passwords do not match",
              })}
            />
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
          >
            {!loading && (
              <>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ flex: 1 }}
                >
                  Change password
                </Button>
                <Button variant="outlined" onClick={handleBack} size="large">
                  Back
                </Button>
              </>
            )}
            {loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <CircularProgress size={25} thickness={2} />
              </Box>
            )}
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export const checkPasswords = async ({
  oldPasswordRequired,
  formdata,
  verifyPasswordStrength,
}: {
  oldPasswordRequired: boolean;
  formdata: ChangePasswordFormData;
  verifyPasswordStrength: (password: string) => Promise<any>;
}) => {
  if (
    oldPasswordRequired &&
    formdata.oldpassword &&
    formdata.oldpassword === formdata.newpassword
  ) {
    toast.error(
      "We're sorry, but your new password cannot be the same as your old password. " +
        "Please choose a new password that is different from your previous one. " +
        "Thank you!",
    );
    return false;
  }

  if (formdata.newpassword === "") {
    toast.error("Please enter a new password.");
    return false;
  }

  const strength = await verifyPasswordStrength(formdata.newpassword);

  if (!strength.valid_password) {
    toast.error(
      `The new password must be Stronger. 
              ${strength.warning ? `${strength.warning}` : ""}
              ${
                strength.suggestions.length > 0
                  ? `Suggestions: \n ${strength.suggestions.join("\n")}`
                  : ""
              }
              `,
      { autoClose: 10000 },
    );
    return false;
  }

  if (formdata.newpassword !== formdata.confirmpassword) {
    toast.error("Confirm password must be same as new password");
    return false;
  }

  return true;
};
